import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentAPI, reportsAPI, questionsAPI, shareAPI, exportAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Shield,
  ArrowLeft,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  MinusCircle,
  AlertTriangle,
  FileText,
  BarChart3,
  PieChart,
  Share2,
  Link,
  Copy,
  X,
  Clock,
  Sparkles,
  Table
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import VulcanBadge from '../components/VulcanBadge';

const COLORS = {
  yes: '#22c55e',
  no: '#ef4444',
  na: '#6b7280',
  unanswered: '#334155'
};

const Report = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [scores, setScores] = useState(null);
  const [deficiencies, setDeficiencies] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, [id]);

  const fetchReportData = async () => {
    try {
      const assessmentRes = await assessmentAPI.getDetail(id);
      setAssessment(assessmentRes.data);

      const questionsRes = await questionsAPI.getQuestions(id);
      setQuestions(questionsRes.data.questions || []);

      // Use generic scores endpoint that works for all assessment types
      const scoresRes = await reportsAPI.getScores(id);
      setScores(scoresRes.data);

      const deficienciesRes = await reportsAPI.getDeficiencies(id);
      setDeficiencies(deficienciesRes.data.deficiencies || []);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    setExportingExcel(true);
    try {
      const res = await exportAPI.getExcelData(id);
      const data = res.data;
      
      // Create CSV content
      const headers = ['Question ID', 'Category', 'Question', 'Answer', 'Comment', 'NIST Reference', 'Remediation Status', 'Remediation Notes'];
      const rows = data.data.map(row => [
        row.questionId,
        row.category,
        `"${row.questionText.replace(/"/g, '""')}"`,
        row.answer || '',
        `"${(row.comment || '').replace(/"/g, '""')}"`,
        row.nistReference || '',
        row.remediationStatus || '',
        `"${(row.remediationNotes || '').replace(/"/g, '""')}"`
      ]);
      
      const csvContent = [
        `# ${data.assessmentName} - ${data.assessmentType} Assessment`,
        `# Score: ${data.score}% | Status: ${data.status}`,
        `# Exported: ${new Date(data.exportDate).toLocaleString()}`,
        '',
        headers.join(','),
        ...rows.map(r => r.join(','))
      ].join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${data.assessmentName}-Export.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      notify.success('Excel/CSV export downloaded');
    } catch (error) {
      console.error('Failed to export:', error);
      notify.error('Failed to export data');
    } finally {
      setExportingExcel(false);
    }
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Helper function to get score color
      const getScoreColor = (score) => {
        if (score >= 75) return [34, 197, 94]; // green
        if (score > 50) return [234, 179, 8]; // yellow
        return [239, 68, 68]; // red
      };
      
      // Header with Aspiritech branding
      doc.setFillColor(30, 41, 59);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      // Purple accent bar
      doc.setFillColor(139, 92, 246);
      doc.rect(0, 45, pageWidth, 3, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.text('Aspiritech', 20, 18);
      
      doc.setFontSize(10);
      doc.setTextColor(200, 200, 220);
      doc.text('Masks Down, Shields Up', 20, 26);
      
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('Cybersecurity Assessment Report', 20, 38);
      
      // Assessment name and type badge
      let yPos = 60;
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(18);
      doc.text(assessment?.name || 'Assessment', 20, yPos);
      
      // Type badge
      const typeColors = {
        'HIPAA': [34, 197, 94],
        'CMMC': [59, 130, 246],
        'NIST_CSF': [168, 85, 247]
      };
      const badgeColor = typeColors[assessment?.assessmentType] || [100, 100, 100];
      doc.setFillColor(...badgeColor);
      doc.roundedRect(pageWidth - 50, yPos - 8, 35, 12, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text(assessment?.assessmentType === 'NIST_CSF' ? 'NIST CSF' : assessment?.assessmentType, pageWidth - 48, yPos - 1);
      
      // Score section with color coding
      yPos += 20;
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(20, yPos, pageWidth - 40, 35, 3, 3, 'F');
      
      // Overall Score with color
      const scoreColor = getScoreColor(assessment?.score || 0);
      doc.setTextColor(...scoreColor);
      doc.setFontSize(28);
      doc.text(`${assessment?.score || 0}%`, 30, yPos + 22);
      
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(10);
      doc.text('Overall Score', 30, yPos + 30);
      
      // Stats
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.text(`Questions: ${assessment?.answeredQuestions || 0} of ${assessment?.totalQuestions || 0}`, 90, yPos + 15);
      doc.text(`Status: ${assessment?.status || 'In Progress'}`, 90, yPos + 25);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 70, yPos + 20);
      
      // Score by Category Table
      yPos += 50;
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(14);
      doc.text(assessment?.assessmentType === 'CMMC' ? 'Scores by Domain' : 'Scores by Category', 20, yPos);
      
      yPos += 8;
      const scoreData = assessment?.assessmentType === 'CMMC' 
        ? scores?.domainScores || []
        : scores?.categoryScores || [];
      
      const tableData = scoreData.map(s => [
        s.domain || s.category,
        s.domainName || s.category,
        `${s.score}%`,
        s.yes.toString(),
        s.no.toString(),
        s.na.toString()
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [['Code', 'Name', 'Score', 'Yes', 'No', 'N/A']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
        bodyStyles: { textColor: [30, 41, 59] },
        columnStyles: {
          2: { halign: 'center' },
          3: { halign: 'center', textColor: [34, 197, 94] },
          4: { halign: 'center', textColor: [239, 68, 68] },
          5: { halign: 'center', textColor: [100, 100, 100] }
        },
        didParseCell: function(data) {
          // Color-code the score column
          if (data.column.index === 2 && data.section === 'body') {
            const score = parseFloat(data.cell.text[0]);
            if (score >= 75) {
              data.cell.styles.textColor = [34, 197, 94];
            } else if (score > 50) {
              data.cell.styles.textColor = [234, 179, 8];
            } else {
              data.cell.styles.textColor = [239, 68, 68];
            }
          }
        }
      });
      
      // Deficiencies on new page
      if (deficiencies.length > 0) {
        doc.addPage();
        
        // Deficiencies header
        doc.setFillColor(239, 68, 68);
        doc.rect(0, 0, pageWidth, 25, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.text(`Deficiencies (${deficiencies.length} items requiring attention)`, 20, 16);
        
        const deficiencyData = deficiencies.map((d, i) => [
          i + 1,
          d.questionId,
          d.category,
          d.questionText.length > 70 ? d.questionText.substring(0, 70) + '...' : d.questionText
        ]);
        
        autoTable(doc, {
          startY: 35,
          head: [['#', 'ID', 'Category', 'Question']],
          body: deficiencyData,
          theme: 'striped',
          headStyles: { fillColor: [127, 29, 29], textColor: [255, 255, 255] },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 25 },
            2: { cellWidth: 35 },
            3: { cellWidth: 'auto' }
          }
        });
      }
      
      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Footer bar
        doc.setFillColor(248, 250, 252);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Aspiritech Cybersecurity Assessment Tool • Masks Down, Shields Up`,
          20,
          pageHeight - 6
        );
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - 30,
          pageHeight - 6
        );
      }
      
      doc.save(`${assessment?.name || 'assessment'}-report.pdf`);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setGenerating(false);
    }
  };

  const getAnswerStats = () => {
    const stats = { Y: 0, N: 0, NA: 0, U: 0 };
    questions.forEach(q => {
      stats[q.answer || 'U']++;
    });
    return [
      { name: 'Yes', value: stats.Y, color: COLORS.yes },
      { name: 'No', value: stats.N, color: COLORS.no },
      { name: 'N/A', value: stats.NA, color: COLORS.na },
      { name: 'Unanswered', value: stats.U, color: COLORS.unanswered }
    ];
  };

  const pieData = getAnswerStats();

  const getBarChartData = () => {
    // Use unified categoryScores format from generic endpoint
    return scores?.categoryScores?.map(s => ({
      name: s.category.length > 12 ? s.category.substring(0, 10) + '...' : s.category,
      score: s.score,
      fullName: s.category,
      yes: s.yes,
      no: s.no,
      total: s.total,
      // Color based on score thresholds
      fill: s.score >= 75 ? '#22c55e' : s.score > 50 ? '#eab308' : '#ef4444'
    })) || [];
  };

  const barChartData = getBarChartData();

  // Share link functions
  const checkShareLink = async () => {
    try {
      const response = await shareAPI.getShareLinkStatus(id);
      if (response.data.hasShareLink) {
        setShareLink(response.data);
      }
    } catch (error) {
      console.error('Failed to check share link:', error);
    }
  };

  const handleCreateShareLink = async () => {
    setShareLoading(true);
    try {
      const response = await shareAPI.createShareLink(id, 30);
      setShareLink({
        hasShareLink: true,
        ...response.data
      });
    } catch (error) {
      console.error('Failed to create share link:', error);
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShareLink = async () => {
    setShareLoading(true);
    try {
      await shareAPI.revokeShareLink(id);
      setShareLink(null);
    } catch (error) {
      console.error('Failed to revoke share link:', error);
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = () => {
    const fullUrl = `${window.location.origin}/shared/${shareLink.shareToken}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenShareModal = async () => {
    setShowShareModal(true);
    await checkShareLink();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="report-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-violet-400" />
                Share Assessment Results
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-1 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {shareLink?.hasShareLink ? (
              <div className="space-y-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                    <Link className="w-4 h-4" />
                    Share Link
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/shared/${shareLink.shareToken}`}
                      className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                    />
                    <button
                      onClick={copyShareLink}
                      className="p-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {shareLink.expiresAt && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Clock className="w-4 h-4" />
                    Expires: {new Date(shareLink.expiresAt).toLocaleDateString()}
                  </div>
                )}
                
                <p className="text-sm text-slate-400">
                  Anyone with this link can view the assessment results without logging in.
                </p>
                
                <div className="flex gap-2">
                  <button
                    onClick={handleRevokeShareLink}
                    disabled={shareLoading}
                    className="flex-1 px-4 py-2 border border-red-500 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Revoke Link
                  </button>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-slate-400">
                  Create a shareable link to allow others to view this assessment&apos;s results without logging in.
                </p>
                <div className="bg-slate-700/50 rounded-lg p-4 text-sm text-slate-300">
                  <p className="font-medium mb-2">The shared view includes:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-400">
                    <li>Compliance score and summary</li>
                    <li>Category breakdown</li>
                    <li>Top findings and remediation steps</li>
                  </ul>
                </div>
                <button
                  onClick={handleCreateShareLink}
                  disabled={shareLoading}
                  className="w-full px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {shareLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4" />
                  )}
                  Create Share Link (30 days)
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                data-testid="back-to-assessment"
                onClick={() => navigate(`/assessment/${id}`)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">Assessment Report</h1>
                <p className="text-slate-400 text-sm">{assessment?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleOpenShareModal}
                className="flex items-center gap-2 px-4 py-2 border border-violet-500 text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              <button
                onClick={exportToExcel}
                disabled={exportingExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {exportingExcel ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Table className="w-4 h-4" />
                    Export CSV
                  </>
                )}
              </button>
              <button
                data-testid="export-pdf-btn"
                onClick={generatePDF}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={reportRef}>
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'details', 'deficiencies'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Overall Score</p>
                    <p className={`text-3xl font-bold ${
                      assessment?.score >= 75 ? 'text-green-400' : 
                      assessment?.score > 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>{assessment?.score}%</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    assessment?.score >= 75 ? 'bg-green-500/20' : 
                    assessment?.score > 50 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                  }`}>
                    <BarChart3 className={`w-6 h-6 ${
                      assessment?.score >= 75 ? 'text-green-400' : 
                      assessment?.score > 50 ? 'text-yellow-400' : 'text-red-400'
                    }`} />
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Compliant (Yes)</p>
                    <p className="text-3xl font-bold text-green-400">{pieData.find(p => p.name === 'Yes')?.value || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Deficiencies (No)</p>
                    <p className="text-3xl font-bold text-red-400">{deficiencies.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-400" />
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Progress</p>
                    <p className="text-3xl font-bold text-white">
                      {Math.round((assessment?.answeredQuestions / assessment?.totalQuestions) * 100)}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <PieChart className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Vulcan Level Security Badge - 100% Compliance */}
            {assessment?.score === 100 && (
              <div className="bg-gradient-to-r from-violet-900/50 via-purple-900/50 to-violet-900/50 rounded-xl p-8 border border-violet-500/50 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                  <h3 className="text-2xl font-bold text-white">Perfect Compliance Achieved!</h3>
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                </div>
                <p className="text-violet-200 mb-6">
                  Congratulations! You've achieved 100% compliance on this assessment.
                  You've earned the Vulcan Level Security Badge.
                </p>
                <VulcanBadge 
                  assessmentType={assessment?.assessmentType}
                  assessmentName={assessment?.name}
                  dateAchieved={new Date().toLocaleDateString()}
                  size="large"
                />
              </div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bar Chart - Scores by Domain/Category */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {assessment?.assessmentType === 'CMMC' ? 'Scores by Domain' : 'Scores by Category'}
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartData} layout="vertical" margin={{ left: 0, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis type="number" domain={[0, 100]} stroke="#94a3b8" tickFormatter={(v) => `${v}%`} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        stroke="#94a3b8" 
                        width={0}
                        tick={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value, name, props) => [`${value}%`, props.payload.fullName]}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                      />
                      <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                        {barChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-slate-500 text-xs mt-2 text-center">Hover over bars to see category names</p>
              </div>

              {/* Pie Chart - Answer Distribution */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Answer Distribution</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                      />
                      <Legend />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      {assessment?.assessmentType === 'CMMC' ? 'Domain' : 'Category'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Yes
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                      No
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                      N/A
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Unanswered
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {(assessment?.assessmentType === 'CMMC' 
                    ? scores?.domainScores 
                    : scores?.categoryScores
                  )?.map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-700/50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {row.domain || row.category}
                          </div>
                          {row.domainName && (
                            <div className="text-xs text-slate-400">{row.domainName}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                row.score >= 75 ? 'bg-green-500' : 
                                row.score > 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${row.score}%` }}
                            />
                          </div>
                          <span className={`font-medium ${
                            row.score >= 75 ? 'text-green-400' : 
                            row.score > 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>{row.score}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-green-400">{row.yes}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-red-400">{row.no}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-slate-400">{row.na}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-slate-500">{row.unanswered}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-center text-white">{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Deficiencies Tab */}
        {activeTab === 'deficiencies' && (
          <div className="space-y-4">
            {deficiencies.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No Deficiencies Found</h3>
                <p className="text-slate-400">All answered questions are compliant. Great job!</p>
              </div>
            ) : (
              <>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-400">Attention Required</h3>
                    <p className="text-slate-300 text-sm">
                      {deficiencies.length} question(s) were answered &quot;No&quot;, indicating potential compliance gaps.
                    </p>
                  </div>
                </div>

                {deficiencies.map((def, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-xl p-6 border border-red-500/30">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <XCircle className="w-5 h-5 text-red-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded-full">
                            {def.category}
                          </span>
                          <span className="text-slate-500 text-sm font-mono">{def.questionId}</span>
                        </div>
                        <p className="text-white">{def.questionText}</p>
                        {def.referenceText && (
                          <p className="text-slate-400 text-sm mt-2">Reference: {def.referenceText}</p>
                        )}
                        {def.comment && (
                          <div className="mt-3 p-3 bg-slate-700/50 rounded-lg">
                            <p className="text-sm text-slate-400">Comment:</p>
                            <p className="text-slate-300">{def.comment}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Report;
